import { useEffect, useMemo, useState } from "react"
import { FieldType, FieldValue, FileField, Form, FormField, ModalId, O, option, PriceField, useFormKeyboardNavigation, useUIActions } from "../../../../../features/ui"
import { CashFund, CheckFields, CheckType, PaymentCategory, PaymentType, PaymentTypeField, isSelectablePaymentType, useCashFundLoaders } from "../../../../administration"
import { firstItem } from "../../../../../utils/listUtils"
import { TableName } from "../../../../../tables"
import { dateToString } from "../../../../../features/data-types"
import { SalePaymentItemData } from "../../../services/sale-operation-service/types"
import { saleOperationHelpers } from "../../../hooks/saleOperationHooks"

type Props = {
    salePaymentItem?: SalePaymentItemData
    paymentTypes: PaymentType[]
    defaultPaymentTypeId?: string
    defaultAmount?: number
    defaultAmounts?: Map<string, number>
    defaultCheckName?: string
    maxAmount?: number
    hideAdjustments?: boolean
    roundingMode?: boolean
    onSubmit: (salePaymentItem: Partial<SalePaymentItemData>) => void
}

export const SalePaymentItemForm = (props: Props) => {
    const { salePaymentItem, paymentTypes, defaultPaymentTypeId, defaultAmount, defaultAmounts, defaultCheckName, maxAmount, hideAdjustments, roundingMode, onSubmit } = props
    const createMode = !salePaymentItem

    const findPaymentType = (id?: string) => paymentTypes.find(paymentType => paymentType.id === id)

    // El medio de pago de la operación (defaultPaymentTypeId) se ofrece aunque esté deshabilitado: es con el que se acordó el precio
    const activePaymentTypes = useMemo(() => paymentTypes.filter(paymentType => isSelectablePaymentType(paymentType, salePaymentItem?.paymentTypeId, defaultPaymentTypeId)), [paymentTypes])
    const defaultPaymentType = findPaymentType(salePaymentItem?.paymentTypeId || defaultPaymentTypeId) || firstItem(activePaymentTypes)
    const defaultAmountValue = salePaymentItem?.amount || defaultAmount || (defaultPaymentType && defaultAmounts?.get(defaultPaymentType.id))

    const [paymentType, setPaymentType] = useState<O<PaymentType>>(defaultPaymentType)
    const [amount, setAmount] = useState<O<number>>(defaultAmountValue)
    const [cashFunds, setCashFunds] = useState<CashFund[]>([])
    const [cashFundId, setCashFundId] = useState<O<string>>()
    const [checkType, setCheckType] = useState<O<CheckType>>(salePaymentItem?.checkType)
    const [checkName, setCheckName] = useState<O<string>>(salePaymentItem?.checkName || defaultCheckName)
    const [checkIssueDate, setCheckIssueDate] = useState<O<string>>(salePaymentItem?.checkIssueDate || new Date().toISOString())
    const [checkPaymentDate, setCheckPaymentDate] = useState<O<string>>(salePaymentItem?.checkPaymentDate || new Date().toISOString())
    const [checkTaxId, setCheckTaxId] = useState<O<number>>(salePaymentItem?.checkTaxId)
    const [checkEndorsable, setCheckEndorsable] = useState<O<boolean>>(salePaymentItem?.checkEndorsable)
    const [receiptFile, setReceiptFile] = useState<O<File>>(salePaymentItem?.receiptFile)
    
    const { paymentReceiptFileName } = saleOperationHelpers

    const cashFundLoaders = useCashFundLoaders()

    const { closeModal } = useUIActions()

    // Keyboard: starts on the first field once the modal finished opening (Bootstrap focuses the modal itself first).
    // Tab moves between the fields and Enter adds the payment item (on "Seleccionar Archivo" it opens the file picker)
    const { containerRef, onKeyDown } = useFormKeyboardNavigation({ autoFocusDelay: 400, enterAction: 'submit' })

    useEffect(() => {
        const newCashFunds = paymentType?.cashFundIds.map(cashFundId => cashFundLoaders().findCashFund(cashFundId)!) || []
        setCashFunds(newCashFunds)
        setCashFundId(newCashFunds.find(cashFund => cashFund.id === salePaymentItem?.cashFundId)?.id || firstItem(newCashFunds)?.id)
        paymentType && setAmount(salePaymentItem?.amount || defaultAmount || defaultAmounts?.get(paymentType.id))
    }, [paymentType])

    const nonCreditPaymentTypes = useMemo(() => activePaymentTypes.filter(paymentType => paymentType.category !== PaymentCategory.CREDIT_ACCOUNT), [activePaymentTypes])

    const isCheck = (paymentTypeParam?: PaymentType) => paymentTypeParam?.category === PaymentCategory.CHECK

    const onChangePaymentType = (value?: FieldValue) => setPaymentType(findPaymentType(value as O<string>))

    const onChangeCashFund = (value?: FieldValue) => setCashFundId(value as O<string>)

    const onChangeAmount = (value?: FieldValue) => setAmount(value as O<number>)

    const onChangeCheckField = (fieldName: string) => (value?: FieldValue) => {
        new Map<string, (value?: FieldValue) => void>()
            .set('type', (value?: FieldValue) => setCheckType(value as O<CheckType>))
            .set('name', (value?: FieldValue) => setCheckName(value as O<string>))
            .set('amount', (value?: FieldValue) => setAmount(value as O<number>))
            .set('issueDate', (value?: FieldValue) => setCheckIssueDate(dateToString(value)))
            .set('paymentDate', (value?: FieldValue) => setCheckPaymentDate(dateToString(value)))
            .set('taxId', (value?: FieldValue) => setCheckTaxId(value as O<number>))
            .set('endorsable', (value?: FieldValue) => setCheckEndorsable(value as O<boolean>))
            .get(fieldName)?.(value)
    }

    const handleCancel = () => closeModal(ModalId.SALE_PAYMENT_ITEM_FORM)

    const roundingAmountField: FormField = {
        name: 'amount',
        type: FieldType.PRICE,
        label: 'Monto de Redondeo',
        render: () => (
            <PriceField
                id="amount"
                value={amount}
                min={0.01}
                max={maxAmount}
                onChange={onChangeAmount}
            />
        )
    }

    const handleRoundingSubmit = () => {
        onSubmit({ isRounding: true, amount: amount! })
        closeModal(ModalId.SALE_PAYMENT_ITEM_FORM)
    }

    const fields: FormField[] = [
        PaymentTypeField({
            defaultValue: paymentType?.id,
            narrow: false,
            paymentTypes: nonCreditPaymentTypes,
            hideAdjustments,
            onChange: onChangePaymentType
        }),
        {
            name: 'receiptUrl',
            type: FieldType.FILE,
            label: 'Comprobante de Pago',
            hint: 'Formato: PDF',
            render: () => (
                <FileField
                    id="receiptUrl"
                    value={receiptFile}
                    fileName={`${paymentReceiptFileName()}.pdf`}
                    onChange={setReceiptFile}
                />
            )
        }
    ]
    if (cashFunds.length > 0) {
        const cashFundField = {
            name: 'cashFundId',
            type: FieldType.TABLE,
            table: TableName.CASH_FUNDS,
            label: 'Caja Destino',
            value: cashFundId,
            options: cashFunds.map(cashFund => option(cashFund.id, cashFund.name)),
            onChange: onChangeCashFund
        }
        const amountField = {
            name: 'amount',
            type: FieldType.PRICE,
            label: 'Monto Recibido',
            value: amount,
            min: 0.01,
            max: maxAmount,
            onChange: onChangeAmount
        }
        fields.splice(1, 0, cashFundField, amountField)
    } else if (isCheck(paymentType)) {
        const checkFields = CheckFields(
            {
                type: checkType,
                name: checkName,
                amount: amount,
                issueDate: checkIssueDate,
                paymentDate: checkPaymentDate,
                taxId: checkTaxId,
                endorsable: checkEndorsable
            },
            { amount },
            maxAmount,
            onChangeCheckField
        )
        fields.splice(1, 0, ...checkFields)
    }
    
    const handleSubmit = () => {
        const paymentTypeId = paymentType!.id
        const isCheckType = isCheck(findPaymentType(paymentTypeId))
        onSubmit({
            paymentTypeId: paymentType!.id,
            amount: amount!,
            ...(!isCheckType && {
                cashFundId: cashFundId!
            }),
            ...(isCheckType && {
                checkType: checkType!,
                checkName: checkName!,
                checkIssueDate: checkIssueDate!,
                checkPaymentDate: checkPaymentDate!,
                checkTaxId: checkTaxId!,
                checkEndorsable: checkEndorsable!
            }),
            receiptFile
        })
        closeModal(ModalId.SALE_PAYMENT_ITEM_FORM)
    }

    return (
        <div ref={containerRef} onKeyDown={onKeyDown}>
            {roundingMode ? (
                <Form
                    formId="sale-payment-item-form"
                    fields={[roundingAmountField]}
                    createMode={false}
                    onSubmit={handleRoundingSubmit}
                    onCancel={handleCancel}
                />
            ) : (
                <Form
                    formId="sale-payment-item-form"
                    fields={fields}
                    createMode={createMode}
                    createLabel="Agregar"
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            )}
        </div>
    )
}
